import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { mutate } from 'swr'
import { API_URL } from './api'
import type { StreamEvent } from '../types'

type StreamContextValue = {
  connected: boolean
  events: StreamEvent[]
  clear: () => void
}

const StreamContext = createContext<StreamContextValue | null>(null)

const eventTypes = [
  'article.new', 'article.updated', 'story.created', 'story.updated', 'event.created', 'event.updated',
  'fact.new', 'fact.changed', 'fact.conflict', 'fact.corrected', 'source.new', 'feed.blocked',
  'feed.reachable', 'feed.unreachable', 'telegram.message', 'telegram.channel.health',
  'google_news.seen', 'google_search.seen', 'indexing.degraded', 'warp.error',
  'intelligence.updated', 'indexing.updated',
]

export function StreamProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const [connected, setConnected] = useState(false)
  const [events, setEvents] = useState<StreamEvent[]>([])
  useEffect(() => {
    if (!enabled) return
    const stream = new EventSource(`${API_URL}/v1/stream`, { withCredentials: true })
    const listeners = new Map<string, EventListener>()
    const onOpen = () => setConnected(true)
    const onError = () => setConnected(false)
    stream.addEventListener('open', onOpen)
    stream.addEventListener('error', onError)
    for (const type of eventTypes) {
      const listener: EventListener = (raw) => {
        const message = raw as MessageEvent<string>
        let data: unknown = message.data
        try { data = JSON.parse(message.data) } catch { /* keep raw payload */ }
        const received: StreamEvent = { id: Number(message.lastEventId || 0), type, data, receivedAt: new Date() }
        setEvents((current) => [received, ...current].slice(0, 100))
        if (type.startsWith('feed.')) void mutate('/v1/feeds')
        if (type.startsWith('event.')) void mutate('/v1/events')
        if (type.startsWith('story.')) void mutate('/v1/stories')
        if (type === 'intelligence.updated') {
          void mutate('/v1/events')
          void mutate('/v1/stories')
          void mutate((key) => typeof key === 'string' && key.startsWith('/v1/articles'))
        }
        if (type.startsWith('google_') || type.startsWith('indexing.')) void mutate((key) => typeof key === 'string' && key.startsWith('/v1/indexing'))
        if (type.startsWith('telegram.')) void mutate('/v1/telegram/channels')
      }
      listeners.set(type, listener)
      stream.addEventListener(type, listener)
    }
    return () => {
      stream.removeEventListener('open', onOpen)
      stream.removeEventListener('error', onError)
      for (const [type, listener] of listeners) stream.removeEventListener(type, listener)
      stream.close()
      setConnected(false)
    }
  }, [enabled])
  const clear = useCallback(() => setEvents([]), [])
  const value = useMemo(() => ({ connected, events, clear }), [connected, events, clear])
  return <StreamContext.Provider value={value}>{children}</StreamContext.Provider>
}

export function useStream() {
  const value = useContext(StreamContext)
  if (!value) throw new Error('useStream must be used inside StreamProvider')
  return value
}
